import { Logger } from '@shared/common';
import { IsBenignNetworkError, IsBotUserAgent } from '@shared/frontend';
import { useCallback, useEffect, useRef, useState } from 'react';

/** File-local constants. */
const localConstants = { logContext: 'useDashboardData' } as const;

/** Result shape returned by useDashboardData. */
export interface IAsyncDataResult<T> {
  data: T | null;
  loading: boolean;
  error: unknown | null;
  reload: () => void;
}

/**
 * Loads asynchronous data with abort handling and a reload trigger.
 *
 * @param loader - Function that performs the fetch given an abort signal. Pass null to skip loading.
 * @param deps - Dependency list that retriggers the load when changed.
 * @returns The async data result.
 */
export function useDashboardData<T>(loader: ((signal: AbortSignal) => Promise<T>) | null, deps: ReadonlyArray<unknown>): IAsyncDataResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(loader !== null);
  const [error, setError] = useState<unknown | null>(null);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    const currentLoader = loaderRef.current;
    if (!currentLoader) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    currentLoader(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted && Logger.GetErrorName(err) !== 'AbortError') {
          // Crawler-aborted or offline network failures are expected noise (bots render the page and abandon fetches); record them as logs, not errors.
          if (IsBotUserAgent() || IsBenignNetworkError(err)) Logger.Log(localConstants.logContext, Logger.GetErrorMessage(err));
          else Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
          setError(err);
          setLoading(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  return { data, loading, error, reload };
}
